import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";
import { createTouchOpenAutoFocusHandler } from "@/lib/touchAutoFocus";
import {
  SHEET_CLOSE_CLASS,
  SheetCloseIcon,
  SheetGrabZone,
  SHEET_GRAB_ZONE_CLASS,
  SHEET_MAX_H_CLASS,
  SHEET_RADIUS_TOP_CLASS,
  SHEET_ANIM_RISE,
  SHEET_OVERLAY_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";
import { useSheetFitClass } from "@/hooks/use-sheet-fit";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(SHEET_OVERLAY_CLASS, className)}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          // Teléfono (<768): hoja inferior a todo el ancho con la subida canónica.
          // Tableta/escritorio (md+): ventana centrada; los desplazamientos del zoom
          // los trae SHEET_ANIM_RISE, con los mismos -50%/-48% que el translate de
          // centrado — sin ellos el fotograma inicial parte de (0,0) y la ventana
          // cruza en diagonal antes de colocarse.
          "inset-x-0 bottom-0 border-t overflow-hidden " +
          SHEET_RADIUS_TOP_CLASS + " " + SHEET_MAX_H_CLASS + " " + SHEET_ANIM_RISE + " " +
          "md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 " +
          "md:h-auto md:w-[calc(100vw-3rem)] md:max-w-lg md:max-h-[85vh] md:rounded-[24px] md:border",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /**
   * Solo para `side="bottom"`: cuánto ocupa en el teléfono (ver `SheetFit`).
   * Como el interior lo monta el consumidor, `auto` mide el hijo marcado con
   * `data-sheet-scroll` y, si no hay ninguno, el panel.
   */
  fit?: SheetFit;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", className, children, fit = "auto", onOpenAutoFocus, style, ...props }, ref) => {
    const topAnchored = side === "left" || side === "right" || side === "top";
    // El gesto de arrastre cierra a través de un Close oculto: así pasa por el
    // mismo camino de Radix que la X y el Escape.
    const grabCloseRef = React.useRef<HTMLButtonElement>(null);

    const [panel, setPanelNode] = React.useState<HTMLDivElement | null>(null);
    const setPanel = React.useCallback(
      (node: HTMLDivElement | null) => {
        setPanelNode(node);
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );
    // Las hojas laterales ocupan ya todo el alto: crecer no significa nada allí.
    const fitClass = useSheetFitClass({
      fit: side === "bottom" ? fit : "short",
      host: panel,
    });
    // Universal safe-area handling so sheets never collide with the iOS
    // status bar (time/signal/battery) or the home indicator.
    const safeAreaStyle: React.CSSProperties = topAnchored
      ? {
          paddingTop: "var(--safe-area-top, env(safe-area-inset-top, 0px))",
          paddingBottom: "var(--safe-bottom)",
        }
      : {
          paddingBottom: "var(--safe-bottom)",
        };
    const closeStyle: React.CSSProperties | undefined = topAnchored
      ? { top: "calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 0.75rem)" }
      : undefined;
    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={setPanel}
          onOpenAutoFocus={createTouchOpenAutoFocusHandler(onOpenAutoFocus)}
          // `fitClass` AL FINAL, después del `className` del consumidor: mismo
          // grupo de tailwind-merge, gana la última. Ver `useSheetFitClass`.
          className={cn(sheetVariants({ side }), className, fitClass)}
          style={{ ...safeAreaStyle, ...style }}
          {...props}
        >
          {/* Hoja inferior: tirador canónico, en absoluto para no empujar el
              contenido de los consumidores que conservan el p-6 de la base.
              La franja entera es la zona de arrastrar-para-cerrar; el botón
              oculto es cómo el gesto le pide a Radix que cierre. */}
          {side === "bottom" && (
            <>
              <SheetGrabZone
                onClose={() => grabCloseRef.current?.click()}
                className={SHEET_GRAB_ZONE_CLASS}
              />
              <SheetPrimitive.Close ref={grabCloseRef} className="hidden" aria-hidden tabIndex={-1} />
            </>
          )}
          {children}
          <SheetPrimitive.Close
            style={closeStyle}
            className={cn(
              "absolute",
              SHEET_CLOSE_CLASS,
              topAnchored ? "right-5" : "right-[18px] top-4",
            )}
          >
            <SheetCloseIcon />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

// Igual que en dialog.tsx: hueco reservado para la X, que va en `absolute`.
// Sin prefijo: en el teléfono la X también está, y sin el hueco el título se
// metía por debajo de ella.
const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-left pr-[46px]", className)}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-foreground", className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
